import cx from 'classnames'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom'

import { igaoPages } from '../igao/utils'
import { Paper } from '../shared/NarusMaterial'
import SideNav from '../shared/SideNav'
import { findActive } from '../shared/SideNav/utility'

let mobileManagementPermissions = [
  'read',
  'write'
]

let adminRoles = [
  'administrator',
  'executive_director',
  'medical_director'
]

export default function AdminPage () {
  const [ visible, setVisible ] = useState(true)
  let location = useLocation()

  let user = useSelector(state => state?.globalVars?.user)
  let isReady = typeof user !== 'undefined'

  let mobileManagementPermission = useSelector(state => state?.globalVars?.permissions?.mobile_management ?? 'none')
  let isAdmin = adminRoles.includes(user?.app_metadata?.roles.meta.role) ?? false
  let isMobileManager = mobileManagementPermissions.includes(mobileManagementPermission)
  let isIgaoPage = location.pathname.split('/').includes('igao')

  let baseNavStructure = useMemo(
    () => [
      isAdmin && [
        [ 'Administration' ]
      ],
      isAdmin && [
        [ 'User Management' ],
        [ 'Users', '/users' ],
        [ 'External Recipients', '/receivers' ]
      ],
      isAdmin && [
        [ 'Clients and Organizations' ],
        [ 'Organizations', '/organizations' ],
        [ 'Clients', '/clients' ],
        [ 'Client History', '/client-history' ]
      ],
      (isAdmin || isMobileManager) && [
        [ 'Mobile Management' ],
        [ 'Enrollments', '/enrollments' ],
        [ 'Mobile Users', '/mobile-users' ],
        [ 'Mobile History', '/mobile-history' ],
        [ 'Mobile Attempts', '/mobile-attempts' ],
        [ 'Self Enrollment', '/self-enrollment' ]
      ],
      isAdmin && [
        [ 'IGAOs' ],
        [ 'Dynamic IGAOs', '/igao' ],
        [ 'Orphan IGAOs', '/orphan-igaos' ]
      ],
      isAdmin && [
        [ 'Audit' ],
        [ 'Audit Log', '/audit-log' ],
        [ 'Status', '/status' ],
        [ 'Behavioral Health History', '/behavioral' ],
        [ 'YouTurn History', '/youturn' ]
      ],
      isAdmin && [
        [ 'UbaCare' ],
        [ 'Audit Log', '/uba-audit-log' ],
        [ 'LucentRxScripts', '/lucent-rx-scripts' ]
      ],
      isAdmin && [
        [ 'Administrative Records' ],
        [ 'Admin Records', '/admin-records' ],
        [ 'Admin Record Patterns', '/admin-record-patterns' ]
      ],
      isAdmin && [
        [ 'Settings and Configuration' ],
        [ 'Settings', '/settings' ],
        [ 'Configuration', '/config' ]
      ]
    ].filter(Boolean),

    [
      isAdmin,
      isMobileManager
    ]
  )

  let navStructure = useMemo(
    () => [
      ...baseNavStructure,
      isAdmin && igaoPages.map(page => [ page.name, `/igao/${page.id}` ])
    ].filter(Boolean),

    [
      isAdmin,
      baseNavStructure
    ]
  )

  let validBasePaths = useMemo(
    () => navStructure
      .flatMap(v => v)
      .filter(v => v.length > 1)
      .map(v => `/admin${v[1]}`),

    [ navStructure ]
  )

  let canVisitAdminSection = validBasePaths.length > 0
  let canVisitCurrentPath = validBasePaths.some(v => matchPath(v, location.pathname))
  let firstValidPath = validBasePaths[0]

  if (isReady && !canVisitAdminSection) {
    return (
      <Navigate
        to='/'
        replace
      />
    )
  }

  if (isReady && !canVisitCurrentPath) {
    return (
      <Navigate
        to={firstValidPath}
        replace
      />
    )
  }

  return (
    <div
      className='wrapper wide'
      style={{ marginTop: '40px', marginLeft: 'initial', marginRight: 'initial' }}
    >
      <div className='assessment'>
        <Paper
          height='100%'
          zDepth={1}
          noPadding
          noBackground
          style={{ minHeight: '100%' }}
        >
          <SideNav
            basepath='/admin'
            structure={baseNavStructure}
            visible={visible}
            active={findActive(location)}
            toggle={() => setVisible((prev) => !prev)}
          />
        </Paper>

        <div className={cx('assessment-body', { igao: isIgaoPage })}>
          {isReady && (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  )
}
